---
title: Podpora in odpravljanje težav
sidebar_position: 7
description: Podpora in odpravljanje težav
---

# Podpora in odpravljanje težav

## Pogoste težave in rešitve

#### Težava: Prikaz opazovanj je prazen

**Znaki**:

- V gradniku opazovanj (Observation) se ne prikažejo nobeni satelitski posnetki
- Sporočilo: »Ni razpoložljivih opazovanj«

**Možni vzroki in rešitve**:

1. **Časovno obdobje ne ustreza razpoložljivosti podatkov**:
   - **Rešitev**: Odprite **stranski panel** in pojdite na **Nastavitve**, kjer prilagodite časovno obdobje
   - Preverite, ali časovni razpon zajema obdobja, v katerih so bili zbrani satelitski podatki
   - Poskusite razširiti razpon datumov ali izbrati drugo obdobje

2. **Izbrana plast ni na voljo**:
   - **Rešitev**: V **nogi opazovanj** preklopite na drugo plast opazovanj
   - Nekatere plasti morda niso na voljo za vsa časovna obdobja

3. **Napaka pri obdelavi posnetkov**:
   - **Znak**: Namesto posnetka se znotraj ploščice opazovanja prikaže sporočilo o napaki
   - **Rešitev**: Sporočilo prihaja neposredno iz storitve za obdelavo satelitskih posnetkov — preberite ga za konkretna navodila (npr. neveljavna konfiguracija, vrzel v podatkih, prevelika površina)
   - Če je sporočilo nejasno ali vztraja, se obrnite na svojega administratorja in navedite natančno prikazano besedilo

---

#### Težava: FOI ni viden

**Znaki**:

- FOI se ne prikaže na zemljevidu ali na seznamu
- Iskanje ne vrne rezultatov

**Možni vzroki in rešitve**:

1. **Dovoljenja za obseg**:
   - **Rešitev**: Preverite, ali imate dostop do pravilnega obsega (Scope)
   - Za pridobitev dovoljenj za obseg se obrnite na svojega administratorja
   - Preklopite na obseg, do katerega imate dostop

2. **Preveč oddaljen pogled**:
   - **Rešitev**: Približajte zemljevid
   - Območja zanimanja se morda ne prikažejo, dokler ne dosežete praga približanja (običajno raven približanja 12)

3. **Uporabljeni filtri**:
   - **Rešitev**: Počistite vse aktivne filtre na seznamu FOI
   - Ponastavite iskalne pogoje
   - Preverite, ali filtri morda izključujejo vaš FOI

4. **Napačen ID za FOI**:
   - **Rešitev**: Preverite, ali je ID za FOI pravilen
   - Uporabite celotno obliko referenčnega ID-ja (npr. DEMO.FOI.6999931001)

5. **Plast ni prikazana**:
   - plast je morda izklopljena v izbirniku plasti zemljevida
   - **Rešitev**: Odprite **izbirnik plasti zemljevida** in se prepričajte, da je plast ustreznega tipa FOI vklopljena

---

#### Težava: Potek časovne omejitve za potrditev e-pošte

**Znaki**:

- Potrditveno e-poštno sporočilo ni prispelo
- Povezava je potekla (časovna omejitev 5 minut)

**Možni vzroki in rešitve**:

1. **Poteklo 5-minutno okno**:
   - **Rešitev**: Ponovno zaženite postopek prijave
   - Zahtevajte novo potrditveno e-poštno sporočilo
   - Takoj preverite e-pošto in kliknite povezavo v 5 minutah

2. **E-pošta v mapi z neželeno pošto**:
   - **Rešitev**: Preverite mapo z neželeno ali vsiljeno pošto
   - Poiščite e-poštno sporočilo od noreply@planet.com
   - Označite kot »Ni neželena pošta«, da boste prihodnja sporočila prejemali v mapo »Prejeto«

3. **Napačen e-poštni naslov**:
   - **Rešitev**: Preverite, ali je vaš e-poštni naslov v računu Planet pravilen
   - Posodobite e-poštni naslov in poskusite znova

---

#### Težava: Signali/Markerji se ne prikazujejo

**Znaki**:

- Graf vizualizacije signalov/markerjev je prazen
- Ne prikažejo se nobene črte ali markerji

**Možni vzroki in rešitve**:

1. **Signali/Markerji so skriti v legendi**:
   - **Rešitev**: S klikom na elemente legende vklopite njihovo vidnost
   - Poiščite barvne kvadrate (aktivno) v primerjavi s sivimi (neaktivno)
   - Omogočite signale/markerje, ki jih želite prikazati

2. **Kontekst markerjev ni izbran**:
   - **Rešitev**: Odprite **stranski panel** in pojdite na **Nastavitve**, nato pa v razdelku **Markerji** izberite kontekst markerjev
   - Prepričajte se, da ima kontekst za dano časovno obdobje izračunane markerje

3. **Težava s časovnim obdobjem**:
   - **Rešitev**: Odprite **stranski panel** in pojdite na **Nastavitve**, kjer prilagodite časovno obdobje tako, da zajame obdobja s signalnimi podatki
   - Signali so na voljo le za datume s satelitskimi opazovanji

---

#### Težava: Stanje izdelave svežnja je FAILED

**Znaki**:

- Sveženj (Bundle) v **validacijski konzoli** (Validation) takoj po izdelavi prikazuje stanje **FAILED**
- Dodeljen ni noben paket (Package) ali FOI

**Možni vzroki in rešitve**:

1. **Filtri se ne ujemajo z nobenim FOI**:
   - Če poizvedba, seznam FOI v obliki CSV ali seznam referenčnih ID-jev v obliki CSV ne vrne nobenega veljavnega FOI, izdelava svežnja ne uspe.
   - **Rešitev**: Preverite vsak vnos filtra:
     - **Poizvedba**: razširite ali popravite pogoje poizvedbe.
     - **Seznam FOI v obliki CSV**: potrdite, da so ID-ji veljavni in spadajo v trenutni obseg.
     - **Seznam referenčnih ID-jev v obliki CSV**: potrdite, da referenčni ID-ji obstajajo in pripadajo izbranemu tipu FOI.
   - Po prilagoditvi izdelajte nov sveženj — neuspelih svežnjev ni mogoče ponoviti na mestu.

2. **Poizvedba je preširoka (nasproten problem)**:
   - Poizvedba, ki se ujema z več deset tisoč območji zanimanja, lahko število paketov dvigne nad 1000, tudi pri razumni velikosti paketa.
   - **Rešitev**: Zožite poizvedbo, da se skupno število FOI zmanjša na razpon, kjer število paketov ostane v dovoljeni meji.

---

#### Težava: Osnovna plast zemljevida se ne naloži ali ima napačno ime

Če se osnovna plast ne naloži ali je njeno ime videti napačno oziroma zastarelo, je konfiguracija plasti upravljana na ravni obsega — posamezni uporabniki je ne morejo nadzorovati. Težavo prijavite svojemu **administratorju obsega** in navedite ime plasti, ki ga vidite v izbirniku plasti zemljevida.

## Najboljše prakse

#### Časovno obdobje vedno uskladite z razpoložljivimi podatki

- Pred analizo FOI preverite, za katera časovna obdobja so na voljo podatki
- Časovno obdobje nastavite tako, da ustreza rastni sezoni ali obdobju spremljanja
- Ne predpostavljajte, da so podatki na voljo za vse datume

#### Za potrebe analize uporabite ustrezno plast

- **Naravne barve** (True Color): za naraven videz in vizualno interpretacijo
- **Lažne barve** (False Color): za analizo gostote vegetacije
